/* ==========================================================================
   On-Road Price Modal
   --------------------------------------------------------------------------
   Pricing is pulled LIVE from a Google Sheet, keyed by:
       model (sheet "Brand" col) + colour (sheet "Model Name" col) + state
   Current selection is read from the DOM on every open, so the result is
   correct regardless of the order variant vs. state were changed:
       variant colour -> jawaVariantNames[ active swatch id ]
       state          -> ".state-selector" value (360 section, untouched)
       model          -> jawaProductMeta.sheetModel
   Sheet is fetched lazily on first open and cached (memory + sessionStorage).
   ========================================================================== */
(function () {
  "use strict";

  var MODAL_ID = "jawa-on-road-modal";
  var OPEN_EVENT = "jawa:open-on-road-modal";
  var CLOSE_EVENT = "jawa:close-on-road-modal";
  var CACHE_KEY = "jawa_onroad_sheet_v2";

  var BREAKDOWN = [
    { label: "Ex-Showroom Price", key: "ex_showroom" },
    { label: "RTO Tax", key: "rto_tax" },
    { label: "Registration & Charges", key: "registration" },
    { label: "Handling Charges", key: "handling" },
    { label: "Insurance", key: "insurance" }
  ];

  var HEADER_MAP = {
    brand: "model",
    modelname: "color",
    state: "state",
    exshowroom: "ex_showroom",
    rtotax: "rto_tax",
    registrationcharges: "registration",
    registrationandcharges: "registration",
    registration: "registration",
    regcharges: "registration",
    handlingcharges: "handling",
    insurance: "insurance",
    onroadprice: "on_road"
  };

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* ---------- utilities ---------- */
  function normHeader(h) {
    return String(h == null ? "" : h).toLowerCase().replace(/[^a-z0-9]/g, "");
  }
  function normKey(v) {
    return String(v == null ? "" : v).toLowerCase().replace(/[^a-z0-9]/g, "");
  }
  function money(v) {
    if (v == null || String(v).trim() === "") return null;
    var s = String(v).trim().replace(/\.0+$/, "");
    return s.charAt(0) === "\u20B9" ? s : "\u20B9" + s;
  }
  function escapeHtml(v) {
    return String(v == null ? "" : v)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
  function restartAnimation(el) {
    if (!el) return;
    el.style.animation = "none";
    void el.offsetWidth; // reflow
    el.style.animation = "";
  }

  /* ---------- CSV parsing (quote-aware) ---------- */
  function parseCSV(text) {
    var rows = [], row = [], field = "", inQuotes = false, i = 0;
    text = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    while (i < text.length) {
      var c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        }
        field += c; i++; continue;
      }
      if (c === '"') { inQuotes = true; i++; continue; }
      if (c === ",") { row.push(field); field = ""; i++; continue; }
      if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
      field += c; i++;
    }
    if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
    return rows;
  }

  function buildRows(matrix) {
    if (!matrix || !matrix.length) return [];
    var headerIdx = 0;
    for (var h = 0; h < matrix.length; h++) {
      if (matrix[h].map(normHeader).indexOf("state") > -1) { headerIdx = h; break; }
    }
    var header = matrix[headerIdx].map(normHeader);
    var col = {};
    header.forEach(function (name, idx) {
      var field = HEADER_MAP[name];
      if (field && col[field] === undefined) col[field] = idx; // first match wins (dup on-road col)
    });
    var out = [];
    for (var r = headerIdx + 1; r < matrix.length; r++) {
      var cells = matrix[r];
      if (!cells || !cells.length) continue;
      var get = function (f) {
        return col[f] !== undefined && cells[col[f]] != null ? String(cells[col[f]]).trim() : "";
      };
      var model = get("model"), color = get("color"), state = get("state");
      if (!model && !color && !state) continue;
      out.push({
        model: model, color: color, state: state,
        ex_showroom: get("ex_showroom"), rto_tax: get("rto_tax"),
        registration: get("registration"), handling: get("handling"),
        insurance: get("insurance"), on_road: get("on_road"),
        _nm: normKey(model), _nc: normKey(color), _ns: normKey(state)
      });
    }
    return out;
  }

  /* ---------- sheet URL resolution ---------- */
  function resolveSheetUrl(input) {
    var raw = String(input || "").trim();
    if (!raw) return "";
    if (/output=csv|tqx=out:csv/i.test(raw)) return raw;
    if (raw.indexOf("/d/e/") > -1) return raw;
    var m = raw.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    var id = m ? m[1] : (/^[a-zA-Z0-9-_]{20,}$/.test(raw) ? raw : null);
    if (!id) return raw;
    var g = raw.match(/[?#&]gid=([0-9]+)/);
    var url = "https://docs.google.com/spreadsheets/d/" + id + "/gviz/tq?tqx=out:csv";
    if (g) url += "&gid=" + g[1];
    return url;
  }

  /* ---------- cache ---------- */
  function readCache(url, ttlMin) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (obj.url !== url) return null;
      if (Date.now() - obj.t > (ttlMin || 30) * 60000) return null;
      return obj.rows;
    } catch (e) { return null; }
  }
  function writeCache(url, rows) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ url: url, t: Date.now(), rows: rows })); } catch (e) {}
  }

  /* ---------- lookup ---------- */
  function modelMatch(a, b) {
    if (!a || !b) return false;
    return a === b || a.indexOf(b) > -1 || b.indexOf(a) > -1;
  }
  function findRow(rows, model, color, state) {
    var nc = normKey(color), nm = normKey(model), ns = state ? normKey(state) : null;
    var pool = rows.filter(function (r) { return r._nc === nc && modelMatch(r._nm, nm); });
    if (!pool.length) pool = rows.filter(function (r) { return r._nc === nc; });
    if (!pool.length) return null;
    if (ns) {
      var exact = pool.filter(function (r) { return r._ns === ns; })[0];
      if (exact) return exact;
    }
    return pool.filter(function (r) { return r._ns === "delhi"; })[0] || pool[0];
  }

  /* ========================================================================
     Modal wiring
     ======================================================================== */
  ready(function () {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    var config = window.jawaOnRoadConfig || {};

    var els = {
      dialog: modal.querySelector(".jorm-dialog"),
      closeBtns: modal.querySelectorAll("[data-jorm-close]"),
      brand: modal.querySelector('[data-jorm="brand"]'),
      model: modal.querySelector('[data-jorm="model"]'),
      variant: modal.querySelector('[data-jorm="variant"]'),
      state: modal.querySelector('[data-jorm="state"]'),
      heroState: modal.querySelector('[data-jorm="hero-state"]'),
      heroPrice: modal.querySelector('[data-jorm="hero-price"]'),
      priceBlock: modal.querySelector('[data-jorm="price-block"]'),
      loading: modal.querySelector('[data-jorm="loading"]'),
      rows: modal.querySelector('[data-jorm="rows"]'),
      unavailable: modal.querySelector('[data-jorm="unavailable"]'),
      breakdown: modal.querySelector("[data-jorm-breakdown]"),
      breakdownToggle: modal.querySelector("[data-jorm-breakdown-toggle]"),
      breakdownBody: modal.querySelector("[data-jorm-breakdown-body]")
    };

    var lastFocused = null;
    var isOpen = false;
    var renderToken = 0;
    var sheetPromise = null;

    function meta() { return window.jawaProductMeta || {}; }
    function variantNames() { return window.jawaVariantNames || {}; }

    function currentVariantId() {
      var el =
        document.querySelector(".p360__swatch.active") ||
        document.querySelector(".p360-sticky__swatch.active") ||
        document.querySelector(".color_swatch.active") ||
        document.querySelector(".c360-swatch-item.active") ||
        document.querySelector(".p360__swatch") ||
        document.querySelector(".color_swatch") ||
        document.querySelector(".c360-swatch-item");
      if (!el) return null;
      return el.id || (el.dataset ? el.dataset.variantId : null) || null;
    }

    function currentState() {
      var dd =
        document.querySelector(".p360-state-selector") ||
        document.querySelector(".state-selector");
      return dd && dd.value ? dd.value : null;
    }
    function setText(el, value) { if (el) el.textContent = value; }

    /* ---- view states ---- */
    function showLoading() {
      if (els.loading) els.loading.hidden = false;
      if (els.priceBlock) els.priceBlock.hidden = true;
      if (els.breakdown) els.breakdown.hidden = true;
      if (els.unavailable) els.unavailable.hidden = true;
    }
    function showUnavailable() {
      if (els.loading) els.loading.hidden = true;
      if (els.priceBlock) els.priceBlock.hidden = true;
      if (els.breakdown) els.breakdown.hidden = true;
      if (els.unavailable) els.unavailable.hidden = false;
    }
    function showPrice() {
      if (els.loading) els.loading.hidden = true;
      if (els.priceBlock) els.priceBlock.hidden = false;
      if (els.breakdown) els.breakdown.hidden = false;
      if (els.unavailable) els.unavailable.hidden = true;
    }

    /* ---- sheet loader ---- */
    function loadSheet() {
      if (sheetPromise) return sheetPromise;
      var url = resolveSheetUrl(config.sheetUrl);
      if (!url) return Promise.reject(new Error("No sheet URL configured"));
      var cached = readCache(url, config.cacheTtlMinutes);
      if (cached) { sheetPromise = Promise.resolve(cached); return sheetPromise; }
      sheetPromise = fetch(url, { credentials: "omit" })
        .then(function (res) {
          if (!res.ok) throw new Error("Sheet HTTP " + res.status);
          return res.text();
        })
        .then(function (text) {
          var rows = buildRows(parseCSV(text));
          writeCache(url, rows);
          return rows;
        })
        .catch(function (err) { sheetPromise = null; throw err; });
      return sheetPromise;
    }

    /* ---- render ---- */
    function render() {
      var token = ++renderToken;

      var vId = currentVariantId();
      var m = meta();
      var color = (vId && variantNames()[vId]) || "";

      var brandText = m.brand || "";
      if (brandText && !/motorcycles/i.test(brandText)) {
        brandText += " Motorcycles";
      }
      setText(els.brand, brandText || "\u2014");
      setText(els.model, m.model || "\u2014");
      setText(els.variant, color || "\u2014");

      var state = currentState();
      setText(els.state, state || "\u2014");
      setText(els.heroState, state || "");

      if (!color) { showUnavailable(); return; }

      showLoading();

      loadSheet().then(function (rows) {
        if (token !== renderToken || !isOpen) return;

        var row = findRow(rows, m.sheetModel || m.model, color, state);
        if (!row) { showUnavailable(); return; }

        setText(els.state, row.state || state || "\u2014");
        setText(els.heroState, row.state || state || "");

        var onRoad = money(row.on_road);
        setText(els.heroPrice, onRoad || "\u2014");
        restartAnimation(els.heroPrice);

        if (els.rows) {
          var html = "";
          var idx = 0;
          for (var i = 0; i < BREAKDOWN.length; i++) {
            var val = money(row[BREAKDOWN[i].key]);
            if (val == null) continue;
            html +=
              '<div class="jorm-line" style="--jorm-line-index:' + idx + '">' +
              "<dt>" + escapeHtml(BREAKDOWN[i].label) + "</dt>" +
              "<dd>" + escapeHtml(val) + "</dd></div>";
            idx++;
          }
          html +=
            '<div class="jorm-line jorm-line--total" style="--jorm-line-index:' + idx + '">' +
            "<dt>Total On-Road Price</dt>" +
            "<dd>" + escapeHtml(onRoad || "\u2014") + "</dd></div>";
          els.rows.innerHTML = html;
        }

        // Keep an expanded breakdown sized to the new content.
        if (els.breakdownBody && !els.breakdownBody.hidden) {
          els.breakdownBody.style.height = "";
        }

        showPrice();
      }).catch(function () {
        if (token !== renderToken || !isOpen) return;
        showUnavailable();
      });
    }

    /* ---- breakdown: smooth collapse / expand ---- */
    var animHandler = null;
    function clearAnim() {
      if (animHandler && els.breakdownBody) {
        els.breakdownBody.removeEventListener("transitionend", animHandler);
      }
      animHandler = null;
      if (els.breakdownBody) els.breakdownBody.classList.remove("is-animating");
    }
    function expandBreakdown() {
      var body = els.breakdownBody;
      if (!body) return;
      clearAnim();
      body.hidden = false;
      var target = body.scrollHeight;
      body.style.height = "0px";
      body.classList.add("is-animating");
      void body.offsetHeight;
      body.style.height = target + "px";
      els.breakdownToggle.setAttribute("aria-expanded", "true");
      animHandler = function (e) {
        if (e.propertyName !== "height") return;
        body.style.height = "";
        clearAnim();
      };
      body.addEventListener("transitionend", animHandler);
    }
    function collapseBreakdown() {
      var body = els.breakdownBody;
      if (!body) return;
      clearAnim();
      body.style.height = body.scrollHeight + "px";
      body.classList.add("is-animating");
      void body.offsetHeight;
      body.style.height = "0px";
      els.breakdownToggle.setAttribute("aria-expanded", "false");
      animHandler = function (e) {
        if (e.propertyName !== "height") return;
        body.hidden = true;
        body.style.height = "";
        clearAnim();
      };
      body.addEventListener("transitionend", animHandler);
    }
    if (els.breakdownToggle && els.breakdownBody) {
      els.breakdownToggle.addEventListener("click", function () {
        var expanded = els.breakdownToggle.getAttribute("aria-expanded") === "true";
        if (expanded) collapseBreakdown();
        else expandBreakdown();
      });
    }

    /* ---- open / close (smooth) ---- */
    function open() {
      if (isOpen) { render(); return; }
      lastFocused = document.activeElement;
      isOpen = true;
      render();
      modal.hidden = false;
      void modal.offsetWidth;
      modal.classList.add("is-open");
      document.documentElement.classList.add("jorm-scroll-lock");
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKeydown);
      var focusTarget = modal.querySelector("[data-jorm-close]") || els.dialog;
      if (focusTarget) focusTarget.focus();
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      modal.classList.remove("is-open");
      document.documentElement.classList.remove("jorm-scroll-lock");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeydown);
      var done = function (e) {
        if (e && e.target !== modal) return;
        if (!isOpen) modal.hidden = true;
        modal.removeEventListener("transitionend", done);
      };
      modal.addEventListener("transitionend", done);
      setTimeout(function () { if (!isOpen) modal.hidden = true; }, 500);
      if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === "Escape" || e.keyCode === 27) { close(); return; }
      if (e.key === "Tab") trapFocus(e);
    }
    function trapFocus(e) {
      var focusable = els.dialog.querySelectorAll(
        'a[href], button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var list = Array.prototype.filter.call(focusable, function (el) { return el.offsetParent !== null; });
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---- triggers ---- */
    document.addEventListener(OPEN_EVENT, open);
    document.addEventListener(CLOSE_EVENT, close);
    document.addEventListener("click", function (e) {
      var trigger = e.target.closest && e.target.closest('[data-open-modal="on-road-price"]');
      if (trigger) { e.preventDefault(); open(); }
      var closeBtn = e.target.closest && e.target.closest('[data-jorm-close]');
      if (closeBtn) { e.preventDefault(); close(); }
    });
    Array.prototype.forEach.call(els.closeBtns, function (btn) {
      btn.addEventListener("click", close);
    });
    modal.addEventListener("mousedown", function (e) {
      if (e.target === modal) close();
    });

    window.JawaOnRoadModal = { open: open, close: close, refresh: render };
  });
})();
